import type { ApiHookReturn } from '@frontend/api-client';
import { coalesceToUndefined } from '@frontend/duck-tape';
import { useFeatureValue } from '@frontend/react';

type UseDefaultPromoCodeProps = {
  overridePromoCode?: string;
  retrieveClientHookResult: ApiHookReturn<'useRetrieveClientQuery'>;
};

/**
 * Resolves the default promo code from multiple sources with priority:
 * 1. Explicit override (URL param, route param, prop, etc.)
 * 2. Client's signup promo code
 * 3. Feature flag default
 *
 * This hook only determines the initial promo code - it does not override
 * user actions after they manually enter or clear a promo code.
 */
export const useDefaultPromoCode = ({
  overridePromoCode,
  retrieveClientHookResult,
}: UseDefaultPromoCodeProps): string | undefined => {
  const { data: client, isLoading } = retrieveClientHookResult;
  const { value: ffDefaultPromoCode } = useFeatureValue('default_promotion_code');

  if (isLoading) {
    return undefined;
  }

  return coalesceToUndefined(overridePromoCode || client?.signupPromoCode || ffDefaultPromoCode);
};
